import React, { useState, useEffect, useRef } from 'react';
import { useOpenAI } from '../../hooks/useOpenAI';
import AppIcon from '../../components/AppIcon';


const GermanChatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [streamingMessage, setStreamingMessage] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);

  // OpenAI Integration with German system prompt
  const {
    sendStreamingMessage,
    testConnection,
    isLoading: isTyping,
    error: openaiError,
    lastResponse,
    updateConfig,
    clearError,
    isConnected: openaiConnected
  } = useOpenAI({
    model: 'gpt-4',
    systemPrompt: 'Du bist ein hilfsbereiter KI-Assistent. Antworte auf Deutsch und sei freundlich und professionell. Helfe Benutzern bei ihren Fragen und biete praktische Lösungen an.',
    streamEnabled: true,
    temperature: 0.7
  });

  // German conversation starters
  const conversationStarters = [
    'Eine Frage stellen',
    'Hilfe bei einem Problem',
    'Produktinformationen',
    'Technischer Support'
  ];

  const starterDescriptions = [
    'Unser Bot und unser Team können helfen',
    'Wir lösen Probleme gemeinsam',
    'Erfahren Sie mehr über unsere Produkte',
    'Technische Unterstützung erhalten'
  ];

  // Initialize connection on mount
  useEffect(() => {
    const initializeConnection = async () => {
      try {
        await testConnection();
      } catch (error) {
        console.warn('Failed to test OpenAI connection:', error);
      }
    };
    
    initializeConnection();
  }, [testConnection]);

  // Auto scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle sending messages
  const handleSendMessage = async (messageContent) => {
    if (!messageContent?.trim()) return;

    // Clear any previous errors
    clearError();
    setShowSuggestions(false);

    const userMessage = {
      id: Date.now(),
      content: messageContent,
      isUser: true,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setStreamingMessage('');
    setInputMessage('');

    try {
      let fullStreamedContent = '';

      // Use streaming for better user experience
      await sendStreamingMessage(
        messageContent,
        (chunk, fullContent) => {
          fullStreamedContent = fullContent;
          setStreamingMessage(fullContent);
        }
      );
      
      // Add final message after streaming completes
      if (lastResponse && !lastResponse?.error && fullStreamedContent) {
        const botMessage = {
          id: Date.now() + 1,
          content: fullStreamedContent,
          isUser: false,
          timestamp: Date.now()
        };
        
        setMessages(prev => [...prev, botMessage]);
        setStreamingMessage('');
      } else if (lastResponse?.error) {
        const errorMessage = {
          id: Date.now() + 1,
          content: 'Entschuldigung, beim Bearbeiten Ihrer Anfrage ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
          isUser: false,
          timestamp: Date.now(),
          isError: true
        };
        
        setMessages(prev => [...prev, errorMessage]);
        setStreamingMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        content: 'Entschuldigung, beim Bearbeiten Ihrer Anfrage ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        isUser: false,
        timestamp: Date.now(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setStreamingMessage('');
    }
  };

  const handleInputSubmit = () => {
    if (inputMessage?.trim()) {
      handleSendMessage(inputMessage?.trim());
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleInputSubmit();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const MessageBubble = ({ message, isUser }) => (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      {!isUser && (
        <div className="flex-shrink-0 mr-3">
          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
            <AppIcon name="Bot" size={16} className="text-white" />
          </div>
        </div>
      )}
      
      <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-2xl ${
        isUser 
          ? 'bg-blue-600 text-white' 
          : message?.isError 
            ? 'bg-red-900/50 text-red-100 border border-red-700' :'bg-gray-800 text-gray-100'
      }`}>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{message?.content}</div>
        <div className="text-xs mt-2 opacity-70">
          {formatTime(message?.timestamp)}
        </div>
      </div>
      
      {isUser && (
        <div className="flex-shrink-0 ml-3">
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
            <AppIcon name="User" size={16} className="text-gray-300" />
          </div>
        </div>
      )}
    </div>
  );

  const TypingIndicator = () => (
    <div className="flex justify-start mb-4">
      <div className="flex-shrink-0 mr-3">
        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
          <AppIcon name="Bot" size={16} className="text-white" />
        </div>
      </div>
      
      <div className="bg-gray-800 px-4 py-3 rounded-2xl">
        <div className="flex space-x-1">
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        </div>
      </div>
    </div>
  );

  const StreamingMessage = () => (
    <div className="flex justify-start mb-4">
      <div className="flex-shrink-0 mr-3">
        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
          <AppIcon name="Bot" size={16} className="text-white" />
        </div>
      </div>
      
      <div className="bg-gray-800 text-gray-100 px-4 py-3 rounded-2xl max-w-xs lg:max-w-md">
        <div className="text-sm whitespace-pre-wrap leading-relaxed">
          {streamingMessage}
          <span className="inline-block w-2 h-4 bg-blue-400 ml-1 animate-pulse"></span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-black text-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gray-900/50 border-b border-gray-800">
        <div className="flex items-center space-x-3">
          {/* Logo placeholder - white logo as described */}
          <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
            <span className="text-black font-bold text-sm">AI</span>
          </div>
          <div>
            <h3 className="font-semibold text-white">KI-Assistent</h3>
            <p className="text-xs text-gray-400">Online</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className={`w-2 h-2 rounded-full ${openaiConnected ? 'bg-green-400' : 'bg-red-400'}`}></div>
          <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
            <AppIcon name="X" size={18} className="text-gray-400" />
          </button>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 bg-black">
        {/* Initial greeting and suggestions */}
        {messages?.length === 0 && showSuggestions && (
          <div className="space-y-6">
            {/* Greeting */}
            <div className="text-center space-y-4 pt-8">
              <h1 className="text-3xl font-light text-white">Hi</h1>
              <p className="text-xl text-gray-300 max-w-sm mx-auto">
                Was können wir für Sie tun?
              </p>
            </div>

            {/* Suggestion Cards */}
            <div className="space-y-3 max-w-md mx-auto">
              {conversationStarters?.map((starter, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(starter)}
                  className="w-full p-4 bg-gray-900 hover:bg-gray-800 border border-gray-700 rounded-xl transition-all duration-200 text-left group"
                  disabled={isTyping}
                >
                  <div className="flex items-start space-x-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <AppIcon name="MessageSquare" size={16} className="text-white" />
                    </div>
                    <div>
                      <div className="text-white font-medium mb-1">{starter}</div>
                      <div className="text-sm text-gray-400 group-hover:text-gray-300">
                        {starterDescriptions?.[index]}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Promotional Card */}
            <div className="max-w-md mx-auto mt-8">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-center">
                <div className="text-white font-semibold text-lg mb-2">Built For You</div>
                <div className="text-blue-100 text-sm mb-4">
                  Watch our major product launch on-demand
                </div>
                <div className="text-blue-100 text-xs">
                  A new era of Insights has arrived
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Messages */}
        {messages?.map((message) => (
          <MessageBubble 
            key={message?.id} 
            message={message} 
            isUser={message?.isUser} 
          />
        ))}

        {/* Streaming Message */}
        {streamingMessage && <StreamingMessage />}

        {/* Typing Indicator */}
        {isTyping && !streamingMessage && <TypingIndicator />}

        {/* Connection Error */}
        {openaiError && (
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-900 mb-2">
              <AppIcon name="AlertTriangle" size={16} className="text-yellow-400" />
            </div>
            <p className="text-sm text-yellow-400 mb-2">Verbindungsfehler</p>
            <p className="text-xs text-gray-400">
              Überprüfen Sie Ihre Internetverbindung oder versuchen Sie es später erneut.
            </p>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Bottom Navigation */}
      <div className="border-t border-gray-800 bg-gray-900/50">
        {/* Navigation Icons */}
        <div className="flex justify-center items-center py-3 px-6 space-x-8">
          <button className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-gray-800 transition-colors">
            <AppIcon name="Home" size={20} className="text-blue-400" />
            <span className="text-xs text-blue-400">Home</span>
          </button>
          
          <button className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-gray-800 transition-colors relative">
            <AppIcon name="MessageSquare" size={20} className="text-gray-400" />
            <span className="text-xs text-gray-400">Nachrichten</span>
            {/* Notification badge */}
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
              <span className="text-xs text-white font-medium">3</span>
            </div>
          </button>
          
          <button className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-gray-800 transition-colors">
            <AppIcon name="HelpCircle" size={20} className="text-gray-400" />
            <span className="text-xs text-gray-400">Hilfe</span>
          </button>
          
          <button className="flex flex-col items-center space-y-1 p-2 rounded-lg hover:bg-gray-800 transition-colors">
            <AppIcon name="Newspaper" size={20} className="text-gray-400" />
            <span className="text-xs text-gray-400">News</span>
          </button>
        </div>

        {/* Input Area */}
        <div className="p-4">
          <div className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nachricht eingeben..."
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 text-sm"
                disabled={isTyping}
              />
            </div>
            
            <button
              onClick={handleInputSubmit}
              disabled={!inputMessage?.trim() || isTyping}
              className="flex-shrink-0 p-3 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <AppIcon name="Send" size={16} />
            </button>
          </div>
          
          {!openaiConnected && (
            <p className="text-xs text-red-400 mt-2 text-center">
              Verbindung verloren. Versuche erneut zu verbinden...
            </p>
          )}
        </div>

        {/* Collapse button */}
        <div className="flex justify-center pb-2">
          <button className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
            <AppIcon name="ChevronDown" size={16} className="text-gray-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GermanChatbot;